'use client'

import React, { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Modal } from '@/components/ui/modal'
import Button from '@/components/ui/button/Button'
import Label from '@/components/form/Label'
import type { CmsSection } from '@/types/domain'
import {
  getKnownSectionSpec,
  parseKnownSection,
  serializeKnownSection,
  WEEK_DAYS,
  type FieldSpec,
  type ListFieldSpec,
  type ScalarFieldSpec,
} from './knownSections'

const inputClass =
  'mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20'

export interface CmsSectionSubmitValues {
  id?: string
  page: string
  key: string
  title: string
  contentJson: string
  sortOrder: number
  published: boolean
  backgroundColor: string | null
  textColor: string | null
}

interface CmsSectionModalProps {
  isOpen: boolean
  onClose: () => void
  page: string
  /** null = creating a new section. */
  section: CmsSection | null
  defaultSortOrder: number
  onSubmit: (values: CmsSectionSubmitValues) => Promise<void>
}

function ScalarInput({
  field,
  value,
  onChange,
  disabled,
}: {
  field: ScalarFieldSpec
  value: unknown
  onChange: (v: string | number) => void
  disabled?: boolean
}) {
  if (field.kind === 'boolean') {
    return (
      <label className="mt-1 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={value !== false}
          onChange={(e) => (onChange as (v: unknown) => void)(e.target.checked)}
          disabled={disabled}
          className="h-4 w-4 rounded border-gray-300"
        />
        {field.label}
      </label>
    )
  }
  if (field.kind === 'textarea') {
    return (
      <textarea
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        rows={3}
        disabled={disabled}
        className={inputClass}
      />
    )
  }
  return (
    <input
      type={field.kind === 'number' ? 'number' : 'text'}
      value={(value as string | number) ?? ''}
      onChange={(e) => onChange(field.kind === 'number' ? e.target.valueAsNumber || 0 : e.target.value)}
      placeholder={field.placeholder}
      disabled={disabled}
      className={inputClass}
    />
  )
}

/** Simple repeatable card-list editor for array-of-object fields (stats, values, testimonials, …) —
 * same "add row / remove row" shape used elsewhere in admin-dashboard for list-like data, just
 * generalized to arbitrary sub-fields instead of a single value. */
function ListEditor({
  field,
  items,
  onChange,
  disabled,
}: {
  field: ListFieldSpec
  items: Record<string, unknown>[]
  onChange: (items: Record<string, unknown>[]) => void
  disabled?: boolean
}) {
  const addItem = () => {
    const blank: Record<string, unknown> = {}
    for (const f of field.itemFields) blank[f.key] = f.kind === 'number' ? 0 : ''
    onChange([...items, blank])
  }
  const removeItem = (index: number) => onChange(items.filter((_, i) => i !== index))
  const updateItem = (index: number, key: string, value: unknown) =>
    onChange(items.map((item, i) => (i === index ? { ...item, [key]: value } : item)))

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">
              {field.itemLabel} {index + 1}
            </span>
            <button
              type="button"
              onClick={() => removeItem(index)}
              disabled={disabled}
              className="text-red-500 hover:text-red-600 disabled:opacity-40"
              title={`Remove ${field.itemLabel.toLowerCase()}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          {field.itemFields.map((f) => (
            <div key={f.key}>
              <Label htmlFor={`${field.key}-${index}-${f.key}`} className="text-xs">
                {f.label}
              </Label>
              <ScalarInput field={f} value={item[f.key]} onChange={(v) => updateItem(index, f.key, v)} disabled={disabled} />
            </div>
          ))}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        disabled={disabled}
        className="flex items-center gap-1 text-sm text-blue-600 hover:underline disabled:opacity-40"
      >
        <Plus className="h-3.5 w-3.5" /> Add {field.itemLabel.toLowerCase()}
      </button>
    </div>
  )
}

function WeekHoursEditor({
  value,
  onChange,
  disabled,
}: {
  value: Record<string, string>
  onChange: (v: Record<string, string>) => void
  disabled?: boolean
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {WEEK_DAYS.map((day) => (
        <div key={day}>
          <Label htmlFor={`hours-${day}`} className="text-xs capitalize">
            {day}
          </Label>
          <input
            id={`hours-${day}`}
            value={value?.[day] ?? ''}
            onChange={(e) => onChange({ ...value, [day]: e.target.value })}
            placeholder="9:00 AM - 5:00 PM"
            disabled={disabled}
            className={inputClass}
          />
        </div>
      ))}
    </div>
  )
}

function FriendlyFields({
  fields,
  state,
  onChange,
  disabled,
}: {
  fields: FieldSpec[]
  state: Record<string, unknown>
  onChange: (key: string, value: unknown) => void
  disabled?: boolean
}) {
  return (
    <>
      {fields.map((field) => (
        <div key={field.key}>
          {field.kind === 'weekHours' ? (
            <>
              <Label>{field.label}</Label>
              <WeekHoursEditor
                value={(state[field.key] as Record<string, string>) ?? {}}
                onChange={(v) => onChange(field.key, v)}
                disabled={disabled}
              />
            </>
          ) : field.kind === 'list' ? (
            <>
              <Label>{field.label}</Label>
              <ListEditor
                field={field}
                items={(state[field.key] as Record<string, unknown>[]) ?? []}
                onChange={(v) => onChange(field.key, v)}
                disabled={disabled}
              />
            </>
          ) : (
            <>
              {field.kind !== 'boolean' && <Label htmlFor={field.key}>{field.label}</Label>}
              <ScalarInput field={field} value={state[field.key]} onChange={(v) => onChange(field.key, v)} disabled={disabled} />
            </>
          )}
        </div>
      ))}
    </>
  )
}

/**
 * Create/edit modal for a CmsSection. Renders a friendly per-field form for any (page, key)
 * in `knownSections.ts`'s registry; falls back to the original raw-JSON textarea for
 * everything else, so unmapped sections stay fully editable.
 */
export default function CmsSectionModal({ isOpen, onClose, page, section, defaultSortOrder, onSubmit }: CmsSectionModalProps) {
  const [title, setTitle] = useState('')
  const [key, setKey] = useState('')
  const [sortOrder, setSortOrder] = useState(0)
  const [published, setPublished] = useState(true)
  const [backgroundColor, setBackgroundColor] = useState('')
  const [textColor, setTextColor] = useState('')
  const [friendlyState, setFriendlyState] = useState<Record<string, unknown>>({})
  const [rawJson, setRawJson] = useState('{}')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Reset the meta fields to the section being edited (or blank, for create) on open.
  useEffect(() => {
    if (!isOpen) return
    setTitle(section?.title ?? '')
    setKey(section?.key ?? '')
    setSortOrder(section?.sortOrder ?? defaultSortOrder)
    setPublished(section?.published ?? true)
    setBackgroundColor(section?.backgroundColor ?? '')
    setTextColor(section?.textColor ?? '')
    setError('')
  }, [isOpen, section, defaultSortOrder])

  // Re-derive the content editor whenever (page, key) changes while open — from the existing
  // section's contentJson if the key wasn't changed, otherwise blank/default for that shape.
  useEffect(() => {
    if (!isOpen) return
    const spec = getKnownSectionSpec(page, key)
    const matchesExisting = !!section && section.key === key
    if (spec) {
      setFriendlyState(parseKnownSection(spec, matchesExisting ? section!.contentJson : undefined))
    } else {
      try {
        setRawJson(matchesExisting ? JSON.stringify(JSON.parse(section!.contentJson || '{}'), null, 2) : '{}')
      } catch {
        setRawJson(matchesExisting ? section!.contentJson : '{}')
      }
    }
    // Only (page, key, isOpen) should re-derive content — `section` changing identity between
    // renders (e.g. after a save-triggered refetch) shouldn't blow away in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, page, key])

  const spec = getKnownSectionSpec(page, key)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return setError('Title is required')
    if (!key.trim()) return setError('Section key is required')

    let contentJson: string
    if (spec) {
      contentJson = serializeKnownSection(spec, friendlyState)
    } else {
      try {
        contentJson = JSON.stringify(JSON.parse(rawJson))
      } catch {
        setError('Content must be valid JSON')
        return
      }
    }

    setSubmitting(true)
    setError('')
    try {
      await onSubmit({
        id: section?.id,
        page,
        key: key.trim(),
        title: title.trim(),
        contentJson,
        sortOrder,
        published,
        backgroundColor: backgroundColor || null,
        textColor: textColor || null,
      })
      onClose()
    } catch (err) {
      setError((err as Error)?.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl">
      <div className="relative max-h-[85vh] w-full overflow-y-auto rounded-2xl bg-white p-6 dark:bg-gray-900">
        <div className="mb-6">
          <h4 className="text-xl font-semibold text-gray-800 dark:text-white/90">
            {section ? 'Edit Section' : `New Section (${page})`}
          </h4>
          {spec && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{spec.label} — friendly form</p>}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cms-title" required>
                  Title
                </Label>
                <input
                  id="cms-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={submitting}
                  className={inputClass}
                />
              </div>
              <div>
                <Label htmlFor="cms-key" required>
                  Section key
                </Label>
                <input
                  id="cms-key"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="e.g. hero, mission, team"
                  disabled={submitting}
                  className={inputClass}
                />
              </div>
            </div>

            {spec ? (
              <FriendlyFields
                fields={spec.fields}
                state={friendlyState}
                onChange={(fkey, value) => setFriendlyState((prev) => ({ ...prev, [fkey]: value }))}
                disabled={submitting}
              />
            ) : (
              <div>
                <Label htmlFor="cms-content">Content (JSON)</Label>
                <textarea
                  id="cms-content"
                  value={rawJson}
                  onChange={(e) => setRawJson(e.target.value)}
                  disabled={submitting}
                  rows={8}
                  className={`${inputClass} font-mono`}
                />
                <p className="mt-1 text-xs text-gray-400">
                  No friendly form for &quot;{page}/{key || '…'}&quot; yet — edit the raw JSON directly.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cms-bg-color">Background color</Label>
                <input
                  id="cms-bg-color"
                  type="color"
                  value={backgroundColor || '#ffffff'}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  disabled={submitting}
                  className="mt-1 h-10 w-full rounded-lg border border-gray-300 dark:border-gray-600"
                />
                {backgroundColor && (
                  <button type="button" onClick={() => setBackgroundColor('')} className="mt-1 text-xs text-gray-400 hover:underline">
                    Clear (use theme default)
                  </button>
                )}
              </div>
              <div>
                <Label htmlFor="cms-text-color">Text color</Label>
                <input
                  id="cms-text-color"
                  type="color"
                  value={textColor || '#111827'}
                  onChange={(e) => setTextColor(e.target.value)}
                  disabled={submitting}
                  className="mt-1 h-10 w-full rounded-lg border border-gray-300 dark:border-gray-600"
                />
                {textColor && (
                  <button type="button" onClick={() => setTextColor('')} className="mt-1 text-xs text-gray-400 hover:underline">
                    Clear (use theme default)
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cms-sort">Sort order</Label>
                <input
                  id="cms-sort"
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.valueAsNumber || 0)}
                  disabled={submitting}
                  className={inputClass}
                />
              </div>
              <div>
                <Label htmlFor="cms-published">Status</Label>
                <select
                  id="cms-published"
                  value={published ? 'true' : 'false'}
                  onChange={(e) => setPublished(e.target.value === 'true')}
                  disabled={submitting}
                  className={inputClass}
                >
                  <option value="true">Published</option>
                  <option value="false">Draft</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </div>

          <div className="mt-8 flex items-center justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              {section ? 'Save' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
